import { assertBetCents } from './money';
import { REEL_STRIPS } from './reel-strips';
import { createGrid, createGridFromStrips, type ReelStripSet } from './reels';
import { getFreeSpinAward, type FreeSpinAward } from './free-spins';
import type {
  Grid,
  MoneyCents,
  RandomSource,
  ReelStops,
  SpinMode,
  SymbolId,
} from './types';
import { evaluateWins, type LineWin, type WildMultiplierPosition } from './wins';

export interface SpinRequest {
  readonly mode: SpinMode;
  readonly betCents: MoneyCents;
  /** Optional base-game strip set for isolated challenge modes. */
  readonly reelStrips?: ReelStripSet;
}

export interface ResolveSpinRequest extends SpinRequest {
  readonly grid: Grid;
  readonly stops: ReelStops;
  /** Diagnostic-only override used to prove the Legacy POG overlay defect. */
  readonly evaluationGrid?: Grid<SymbolId>;
  readonly wildMultipliers?: readonly WildMultiplierPosition[];
}

export interface SpinResult {
  readonly mode: SpinMode;
  readonly stops: ReelStops;
  readonly grid: Grid;
  readonly lineWins: readonly LineWin[];
  readonly scatterCount: number;
  readonly totalWinCents: MoneyCents;
  readonly freeSpinsAwarded: FreeSpinAward | 0;
}

function assertGridSymbols(grid: Grid): void {
  if (grid.length !== 5 || grid.some((column) => column.length !== 3)) {
    throw new RangeError('Ein Spinresultat muss ein 5×3-Raster besitzen.');
  }
  if (grid.some((column) => column.filter((symbol) => symbol === 'SCATTER').length > 1)) {
    throw new Error('Pro Walze darf höchstens ein Scatter sichtbar sein.');
  }
}

function assertStops(stops: ReelStops, strips: ReelStripSet = REEL_STRIPS): void {
  stops.forEach((stop, reelIndex) => {
    const stripLength = strips[reelIndex]?.length ?? 0;
    if (!Number.isSafeInteger(stop) || stop < 0 || stop >= stripLength) {
      throw new RangeError(`Walzenstopp ${reelIndex + 1} ist ungültig.`);
    }
  });
}

export function resolveSpin(request: ResolveSpinRequest): SpinResult {
  assertBetCents(request.betCents);
  assertGridSymbols(request.grid);
  assertStops(request.stops, request.reelStrips);
  const evaluation = evaluateWins(
    request.evaluationGrid ?? request.grid,
    request.betCents,
    undefined,
    request.wildMultipliers,
  );
  return Object.freeze({
    mode: request.mode,
    stops: Object.freeze([...request.stops]) as unknown as ReelStops,
    grid: request.grid,
    lineWins: evaluation.lineWins,
    scatterCount: evaluation.scatterCount,
    totalWinCents: evaluation.totalWinCents,
    freeSpinsAwarded: getFreeSpinAward(evaluation.scatterCount),
  });
}

export function spin(request: SpinRequest, random: RandomSource): SpinResult {
  if (request.mode === 'free' && request.reelStrips !== undefined) {
    throw new Error('Eigene Walzenstreifen sind ausschließlich im Basisspiel erlaubt.');
  }
  const generated = request.reelStrips === undefined
    ? createGrid(request.mode, random)
    : createGridFromStrips(request.reelStrips, random);
  return resolveSpin({ ...request, ...generated });
}
